import { Link } from "react-router-dom";
import { ArrowUpRight, Hammer, Trees, Truck, Snowflake, Building2, Feather, Search } from "lucide-react";
import { IMAGES } from "../lib/site";
import CTASection from "../components/site/CTASection";

const SERVICES = [
  {
    to: "/property-maintenance",
    title: "Property Maintenance",
    copy: "Repairs, handyman work and 24/7 emergency call-outs.",
    icon: Hammer,
    image: IMAGES.repair,
    span: "md:col-span-6 md:row-span-2",
  },
  {
    to: "/garden-maintenance",
    title: "Garden Maintenance",
    copy: "Grass, hedges, limb removal, general upkeep.",
    icon: Trees,
    image: IMAGES.garden,
    span: "md:col-span-3",
  },
  {
    to: "/clearance",
    title: "Clearance & Removal",
    copy: "Garage, shed, house clear-outs & waste removal.",
    icon: Truck,
    image: IMAGES.clearance,
    span: "md:col-span-3",
  },
  {
    to: "/seasonal",
    title: "Seasonal Services",
    copy: "Snow clearing, festive lights, leaf collection.",
    icon: Snowflake,
    image: IMAGES.christmas,
    span: "md:col-span-3",
  },
  {
    to: "/landlords",
    title: "Landlords & Agents",
    copy: "Your single trusted contractor for every job.",
    icon: Building2,
    image: IMAGES.cottage,
    span: "md:col-span-3",
  },
  {
    to: "/sourcing",
    title: "Property Sourcing & Packages",
    copy: "Deal sourcing, setup & maintenance packages for landlords and investors.",
    icon: Search,
    image: IMAGES.cottage,
    span: "md:col-span-12",
    comingSoon: true,
  },
];

export default function Home() {
  return (
    <>
      {/* Hero */}
      <section
        className="relative w-full min-h-[92vh] overflow-hidden bg-black"
        data-testid="home-hero"
      >
        <img
          src={IMAGES.reeds}
          alt="Norfolk reedbeds"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full min-h-[92vh] flex flex-col justify-end pb-20 md:pb-28">
          <p className="overline text-white/80 mb-6" data-testid="hero-tag">
            Norfolk · Established Craft
          </p>
          <h1
            className="font-serif-display text-white text-6xl sm:text-7xl lg:text-8xl leading-[0.9] max-w-5xl"
            data-testid="home-hero-title"
          >
            Restoring and protecting
            <span className="block italic text-white/90">Norfolk homes.</span>
          </h1>
          <p
            className="mt-8 max-w-xl text-white/85 text-lg leading-relaxed"
            data-testid="home-hero-sub"
          >
            Property maintenance, gardens, clearance and seasonal work — carried
            out with care, resilience and a proud local touch.
          </p>
          <p
            className="mt-4 max-w-xl text-white/75 text-base leading-relaxed"
            data-testid="home-hero-sub-2"
          >
            A local team offering high quality services in a friendly and fast
            manner at competitive prices.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="hero-quote-btn"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors px-8 py-5 text-[11px] tracking-[0.22em] uppercase font-medium"
            >
              Request a Quote <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/story"
              data-testid="hero-story-btn"
              className="inline-flex items-center gap-2 border border-white/50 text-white hover:bg-white hover:text-black transition-colors px-8 py-5 text-[11px] tracking-[0.22em] uppercase font-medium"
            >
              Our Story
            </Link>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section
        className="border-y border-black/10 bg-white"
        data-testid="trust-bar"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { k: "15+", v: "Years of craft" },
              { k: "100+", v: "Jobs completed" },
              { k: "10%", v: "Profits to conservation" },
              { k: "24/7", v: "Emergency call-outs" },
            ].map((s) => (
              <div key={s.v} data-testid={`trust-${s.v.replace(/\s+/g, "-").toLowerCase()}`}>
                <p className="font-serif-display text-4xl sm:text-5xl leading-none">
                  {s.k}
                </p>
                <p className="mt-3 text-xs tracking-[0.2em] uppercase text-neutral-500">
                  {s.v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline text-neutral-500">01 · Introduction</p>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              A single, dependable hand for the home, the garden and everything
              between.
            </h2>
            <p className="mt-8 text-neutral-600 text-lg leading-relaxed max-w-2xl">
              Bittern Property Services is a Norfolk-rooted maintenance company
              built on 15 years of hands-on experience. From urgent repairs to
              seasonal groundwork, we bring one team, one standard, and one
              phone number for every job on your property.
            </p>
          </div>
        </div>
      </section>

      {/* Services Bento */}
      <section
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 fade-up"
        data-testid="services-bento"
      >
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline text-neutral-500 mb-4">02 · What we do</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight">
              Services
            </h2>
          </div>
          <Link
            to="/contact"
            className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.18em] uppercase border-b border-black hover:opacity-70"
            data-testid="services-cta-link"
          >
            Request a Quote <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-[220px] gap-4">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.to}
                to={s.to}
                data-testid={`service-card-${s.to.slice(1)}`}
                className={`group relative overflow-hidden bg-neutral-100 border border-black/5 ${s.span || "md:col-span-3"}`}
              >
                <img
                  src={s.image}
                  alt={s.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                {s.comingSoon && (
                  <span
                    className="absolute top-4 right-4 z-20 bg-white text-black text-[10px] tracking-[0.2em] uppercase font-medium px-3 py-1.5"
                    data-testid="coming-soon-badge"
                  >
                    Coming Soon
                  </span>
                )}
                <div className="relative z-10 h-full min-h-[220px] p-6 md:p-8 flex flex-col justify-between text-white">
                  <div className="flex items-center gap-3">
                    <Icon size={20} strokeWidth={1.5} />
                    <ArrowUpRight
                      size={18}
                      className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                  <div>
                    <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-white/80 max-w-sm">
                      {s.copy}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Story Teaser */}
      <section
        className="bg-neutral-100 border-y border-black/5"
        data-testid="home-story-teaser"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center fade-up">
          <div className="md:col-span-6 order-2 md:order-1">
            <p className="overline text-neutral-500 mb-4 flex items-center gap-2">
              <Feather size={14} /> 03 · Our story
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Named for a Norfolk bird brought back from the brink.
            </h2>
            <p className="mt-8 text-neutral-600 text-lg leading-relaxed">
              The Bittern was once close to extinction. Careful restoration and
              habitat maintenance brought it home to the Broads. That story
              shaped ours — we restore, we maintain, and we protect.
            </p>
            <Link
              to="/story"
              data-testid="story-teaser-link"
              className="mt-8 inline-flex items-center gap-2 text-sm tracking-[0.18em] uppercase border-b border-black hover:opacity-70"
            >
              Read the full story <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-6 order-1 md:order-2 img-hover">
            <img
              src={IMAGES.reeds}
              alt="Norfolk reedbeds"
              className="w-full h-[480px] object-cover"
            />
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
